import { useDispatch, useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { v4 as generateUUID } from 'uuid';
import { useErrorOnNotOkFetchye, useOneDataFetchye } from '@americanexpress/fetchye-amex';
import { ignoreHeadersByKey, useFetchye } from 'fetchye';
import { MODULE_NAME } from '../constants/commonConstants';
import { MEMBERSHIP_DOMAIN_QUERY, membershipDomainHeaders } from '../graphql/membershipQuery';
import { graphqlFetcher } from '../graphql/graphqlFetcher';
import {
  getMembershipDomainApiUrl,
  getNavigationOneCmsUrlSelector,
  getPersonaApiUrl,
} from '../selectors/apiSelectors';
import { NavigationLinks } from '../types/NavigationLinks';
import {
  fetchyeOpts,
  generateReporting,
  getPersonaAccounts,
  personaHeaders,
} from '../utils/apiUtils';
import { convertNavigationLinksToArrays } from '../utils/convertNavigationLinksToArray';
import { HTTP_METHODS } from '../constants/apiConstants';
import { getBenefitsFromPersona } from '../utils/personaUtils';

export const useIsLoggedIn = () => {
  const { data, isLoading, error } = useOneDataFetchye('ReadUserSession.v1', { method: 'GET' });
  return {
    data: !!data?.ok,
    isLoading,
    error,
  };
};

export const useMembershipApi = () => {
  const dispatch = useDispatch();
  const { data: isLoggedIn } = useIsLoggedIn();
  const activeLocale: string = useSelector(getActiveLocaleSelector);
  const membershipDomainApiUrl: string = useSelector(getMembershipDomainApiUrl);

  const fetcherOpts = {
    reporting: {
      moduleID: MODULE_NAME,
      component: 'useMembershipApi',
      fetch: {
        url: membershipDomainApiUrl,
        activeLocale,
      },
    },
  };

  const {
    data,
    isLoading,
    error,
  } = useFetchye(
    { query: MEMBERSHIP_DOMAIN_QUERY },
    {
      ...membershipDomainHeaders({ correlationId: generateUUID(), locale: activeLocale }),
      mapOptionsToKey: ignoreHeadersByKey(['X-User-Auth', 'X-Correlation-Id', 'X-Timestamp']),
      defer: !isLoggedIn,
    },
    graphqlFetcher(dispatch, membershipDomainApiUrl, fetcherOpts)
  );

  return { data, isLoading, error };
};

export const useNavigationLinksHook = () => {
  const navigationLinksOneCmsUrl = useSelector(getNavigationOneCmsUrlSelector);

  const {
    isLoading,
    data,
    error,
  } = useErrorOnNotOkFetchye<NavigationLinks>(
    `${navigationLinksOneCmsUrl}`,
    fetchyeOpts,
    generateReporting(
      'src/apiHooks/useNavigationLinksHook'
    )
  );

  return {
    isLoading,
    data: data?.body ? convertNavigationLinksToArrays(data?.body) : undefined,
    error,
  };
};

export const usePersonaApi = (invokeBenefits : boolean) => {
  const locale = useSelector(getActiveLocaleSelector) as string;
  const {
    data: membershipApiData,
  } = useMembershipApi();
  const personaApiUrl = useSelector(getPersonaApiUrl);

  const cards = getPersonaAccounts(membershipApiData);
  const defer = !(invokeBenefits && !!cards);

  const {
    data,
  } = useErrorOnNotOkFetchye(
    `${personaApiUrl}`,
    {
      ...personaHeaders(locale),
      method: HTTP_METHODS.POST,
      credentials: 'include',
      defer,
      body: JSON.stringify({
        cards,
      }),
    },
    generateReporting(
      'src/apiHooks/usePersonaApi'
    )
  );

  return {
    data: data?.body ? getBenefitsFromPersona(data?.body) : undefined,
  };
};
